<template>
  <main class="booking">
    <el-form
      v-if="getBookingTypesStatus == 'success'"
      v-loading="!initialized || !openTime || !closeTime"
      novalidate="true"
      :inline="true"
      label-width="10em"
      label-position="top"
      :size="formSize"
    >
      <el-form-item label="Game Type" v-if="settings.default_players_type == 'combined'">
        <el-select
          :value="bookingTypeId"
          @input="setBookingTypeId">
          <el-option
            v-for="bt in bookingTypes"
            :key="bt.id"
            :label="bt.name"
            :value="bt.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Pick a date">
        <el-date-picker
          v-if="!isMobile"
          :picker-options="pickerOptions"
          value-format="yyyy-MM-dd"
          :value="date"
          @input="setDate"
          type="date"
          :editable="false"
          placeholder="Pick a day"
        />
        <el-input
          v-if="isMobile"
          class="mobile-date"
          @focus="openMobileDatePicker"
          type="text"
          :readonly="true"
          :value="date"
          prefix-icon="el-icon-date"
        />
      </el-form-item>
      <el-form-item label="Pick a time">
        <mt-datetime-picker
          v-if="isMobile"
          @confirm="confirmMobileDate"
          @cancel="closeMobileDatePicker"
          ref="datePicker"
          type="date"
          v-model="mobileDateTime"
          confirmText="Confirm"
          cancelText="Cancel"
          :startDate="today"
          :startHour="minHour"
          :endHour="maxHour"
         />
        <el-time-select
          v-if="openTime"
          :value="time"
          @input="setTime"
          :editable="false"
          :picker-options="{
            start: timeForComponent(openTime),
            step: timeStep,
            end: timeForComponent(closeTime)
          }"
          placeholder="Select time">
        </el-time-select>
      </el-form-item>
      <el-form-item label="Adults" v-if="settings.default_players_type == 'adults_and_juniors'">
        <el-select
          :value="adults"
          @input="setAdults">
          <el-option
            v-for="(n, i) in 31"
            :key="i"
            :label="i"
            :value="i">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Juniors (under 18)" v-if="settings.default_players_type == 'adults_and_juniors'">
        <el-select
          :value="juniors"
          @input="setJuniors">
          <el-option
            v-for="(n, i) in 31"
            :key="i"
            :label="i"
            :value="i">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Players" v-if="settings.default_players_type == 'combined'">
        <el-select
          :value="adults"
          @input="setAdults">
          <el-option
            v-for="i in playerRange"
            :key="i"
            :label="i"
            :value="i">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="Number of games" v-if="!timedGame">
        <el-input-number
          :value="games"
          @input="setGames"
          :min="minGames"
          :max="maxGames"
        />
      </el-form-item>
      <el-form-item label="Duration" v-if="timedGame && bookingType && bookingType.duration == null">
        <el-select
          :value="duration"
          @input="setDuration">
          <el-option
            v-for="i in [15, 30, 45, 60]"
            :key="i"
            :label="i"
            :value="i">
          </el-option>
        </el-select>
        minutes
      </el-form-item>
      <el-form-item label="Promo code" :error="errors.promo_code">
        <el-input
          :disabled="promoCode != null && promoCode.length > 0"
          v-model="promoCodeValue">
          <el-button v-if="promoCodeValue && !promoCode" slot="append" type="primary" @click="applyPromoCode">Apply</el-button>
          <el-button v-if="promoCode" slot="append" type="primary" @click="removePromoCode">Remove</el-button>
        </el-input>
      </el-form-item>
      <el-form-item label="Filter by deal" v-if="allScenarios && bookingTypes.length == 0">
        <el-select placeholder="Select a deal" @input="setScenarioFilter" :value="scenarioFilter.length && scenarioFilter[0] ? scenarioFilter[0] : null">
          <el-option key="0" label="Any" :value="null" />
          <el-option
            v-for="s in allScenarios"
            :key="s.id"
            :label="s.name"
            :value="s.id">
          </el-option>
        </el-select>
      </el-form-item>
    </el-form>

    <scenarios ref="scenarios" v-if="initialized && !errors.promo_code && (adults + juniors > 0)" :scenarioFilter="scenarioFilter" :date="date" :time="time" :adults="adults" :juniors="juniors" :games="games" :duration="duration" :promoCode="promoCode" :timedGame="timedGame" :bookingType="bookingType" />
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import scenarios from '../components/scenarios'
import roundToNearestMinutes from 'date-fns/roundToNearestMinutes'

import addMinutes from 'date-fns/addMinutes'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import isWithinInterval from 'date-fns/isWithinInterval'
import isAfter from 'date-fns/isAfter'
import isSameDay from 'date-fns/isSameDay'
import dateMixin from '../mixins/dateMixin'
import formMixin from '../mixins/formMixin'
import mobileMixin from '../mixins/mobileMixin'
import { DatetimePicker, Picker } from 'mint-ui'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

export default {
  mixins: [dateMixin, formMixin, mobileMixin],
  props: {
    initialBookingTypeId: Number,
    initialDate: String,
    initialTime: String,
    initialAdults: Number,
    initialJuniors: Number,
    initialGames: Number,
    initialDuration: Number,
    initialScenarioFilter: Array,
    initialPromoCode: String
  },
  methods: {
    search() {
      if (this.ready) {
        this.update()
      }
    },
    applyPromoCode() {
      this.setPromoCode(this.promoCodeValue)
    },
    removePromoCode() {
      this.setPromoCode(null)
      this.promoCodeValue = null
      this.$store.commit('scenarios/setErrors', {})
    },
    confirmMobileDate() {
      this.mobileDateTimeUpdated()
      this.closeMobileDatePicker()
    },
    openMobileDatePicker() {
      this.mobileDateTime = this.dateTime
      this.$refs.datePicker.open()
      disableBodyScroll(this.$refs.datePicker.$el)
    },
    closeMobileDatePicker() {
      enableBodyScroll(this.$refs.datePicker.$el)
    },
    update() {
      this.$store.commit('availability/setAvailability', null)
      if (this.adults == 0 && this.juniors == 0)
        return

      if (this.getBookingTypesStatus != 'success')
        return

      if (!this.timedGame && !this.games)
        return

      if (this.timedGame && !this.duration)
        return

    	this.$store.dispatch('availability/getAvailability', {
        resource_type: this.bookingType ? this.bookingType.resource_type_id : null,
				date: this.dateForApi(this.dateTime),
				time: this.timeForApi(this.dateTime),
				adults: this.adults,
				juniors: this.juniors,
        games: !this.timedGame ? this.games : null,
        duration: this.timedGame ? this.duration : null
			})
		},
    openingHoursUpdated() {
      this.openTime = parse(this.openingHours.date + this.openingHours.open_time, "yyyyMMddHHmm", new Date())
      this.closeTime = parse(this.openingHours.date + this.openingHours.close_time, "yyyyMMddHHmm", new Date())

      if (this.closeTime < this.openTime)
        this.closeTime.setHours(23)

      if (this.time && isWithinInterval(parse(this.openingHours.date + this.time.replace(/:/, ''), "yyyyMMddHHmm", new Date()), { start: this.openTime, end: this.closeTime })) {
        this.search()
      } else {
        const newTime = this.timeForComponent(this.nearestTime)
        if (newTime != this.time)
          this.setTime(this.timeForComponent(this.nearestTime))
        else
          this.search()
      }
    },
    dateUpdated() {
      //console.log('date updated!', this.date, this.initialized)
      if (!this.initialized) return
      if (!this.date) return
      this.mobileDateTime = this.dateTime
    	this.$store.dispatch('openingHours/getOpeningHours', {
        date: this.dateForApi(this.dateTime)
      })
    },
    mobileDateTimeUpdated() {
      //console.log("mobile datetime updated")
      this.setDate(this.dateForComponent(this.mobileDateTime))
      this.setTime(this.timeForComponent(this.mobileDateTime))
    },
    disabledDate(dt) {
      let now = new Date()
      return isAfter(now, dt) && !isSameDay(now, dt)
    },
    ...mapActions('booking', ['setBookingTypeId', 'setDate', 'setTime', 'setAdults', 'setJuniors', 'setGames', 'setDuration', 'setTimedGame', 'setScenarioFilter', 'setPromoCode']),
    initialize() {
    	this.$store.dispatch('bookingTypes/getBookingTypes')
      const propMap = {
        setBookingTypeId: 'initialBookingTypeId',
        //setDate: 'initialDate',
        setTime: 'initialTime',
        setAdults: 'initialAdults',
        setJuniors: 'initialJuniors',
        setGames: 'initialGames',
        setDuration: 'initialDuration',
        setTimedGame: 'initialTimedGame',
        setScenarioFilter: 'initialScenarioFilter',
        setPromoCode: 'initialPromoCode'
      }
      for (const k in propMap) {
        if (this[propMap[k]]) {
          this[k](this[propMap[k]])
        }
      }

      if (this.settings.default_game_type == 'numbered')
        this.setTimedGame(this.settings.default_game_type == 'timed')

      if (this.games == null)
        this.setGames(this.settings.default_games)

      if (this.duration == null)
        this.setDuration(60)

      if (this.adults == null && this.settings.default_players)
        this.setAdults(this.settings.default_players)
    }
	},
  created() {
    //console.log("created!", this.promoCodeValue, this.promoCode, this.initialPromoCode)
    this.promoCodeValue = this.promoCode || this.initialPromoCode
    this.$store.dispatch('scenarios/getAllScenarios')
    this.$store.dispatch('resources/getResources')
    this.initialize()
    this.initialized = true
    if (this.initialDate) {
      //console.log('setting from initial date')
      if (this.date != this.initialDate)
        this.setDate(this.initialDate)
      else
        this.dateUpdated()
    } else if (this.date == null) {
      //console.log('date is null')
      this.setDate(this.dateForComponent(new Date()))
    } else {
      //console.log('date is already set', this.date)
      this.dateUpdated()
    }
  },
  watch: {
    openingHours: 'openingHoursUpdated',
    date: 'dateUpdated',
    time: 'search',
    adults: 'search',
    juniors: 'search',
    games: 'search',
    duration: 'search',
    scenarioFilter: 'search',
    promoCode: 'search',
    //timedGame: 'search',
    getAvailabilityStatus() {
      if (this.getAvailabilityStatus == 'success') {
        this.$refs.scenarios.update()
      }
    },
    getBookingTypesStatus() {
      if (!this.bookingTypeId && this.getBookingTypesStatus == 'success' && this.bookingTypes.length) {
        this.setBookingTypeId(this.bookingTypes[0].id)
      }
    },
    bookingType() {
      if (this.bookingType) {
        this.setDuration(this.bookingType.duration)
        if (this.adults == null)
          this.setAdults(this.bookingType.default_players)

        if (this.bookingType.min_players)
          this.minPlayers = this.bookingType.min_players
        else
          this.minPlayers = 0

        if (this.bookingType.max_players)
          this.maxPlayers = this.bookingType.max_players
        else
          this.maxPlayers = 100

        if (this.adults < this.minPlayers)
          this.setAdults(this.minPlayers)

        if (this.adults > this.maxPlayers)
          this.setAdults(this.maxPlayers)

        if (this.games > this.bookingType.max_games)
          this.setGames(this.bookingType.max_games)

        if (this.games < this.bookingType.min_games)
          this.setGames(this.bookingType.min_games)

        const clamp = (num, min, max) => Math.min(Math.max(num, min), max)

        const nearestTo = parseInt(this.timeStep.split(':')[1])
        const newTime = nearestTo == 0 ?
          (
            format(this.dateTime, 'HH') + ':00'
          )
          :
          (
            nearestTo > 30 ?
            ''
            :
            this.timeForComponent(roundToNearestMinutes(this.dateTime, { nearestTo }))
          )

        this.setTime(newTime)

        if (this.bookingType.scenario_ids)
          this.setScenarioFilter(this.bookingType.scenario_ids)
        else
          this.setScenarioFilter(null)
      }
    }
  },
  computed: {
    ...mapState({
      getBookingTypesStatus: state => state.bookingTypes.status.getBookingTypes,
      getAvailabilityStatus: state => state.availability.status.getAvailability,
      bookingTypes: state => state.bookingTypes.bookingTypes,
      settings: state => state.settings.tenant,
      getFilteredScenariosStatus: state => state.scenarios.status.getFilteredScenarios,
      errors: state => state.scenarios.errors,
      allScenarios: state => state.scenarios.all,
      availability: state => state.availability.all,
      openingHours: state => state.openingHours.all,
      bookingTypeId: state => state.booking.options.bookingTypeId,
      adults: state => state.booking.options.adults,
      juniors: state => state.booking.options.juniors,
      games: state => state.booking.options.games,
      duration: state => state.booking.options.duration,
      //timedGame: state => state.booking.options.timedGame,
      time: state => state.booking.options.time,
      date: state => state.booking.options.date,
      scenarioFilter: state => state.booking.options.scenarioFilter,
      promoCode: state => state.booking.options.promoCode
    }),
    bookingType() {
      return this.bookingTypes.find((o) => o.id == this.bookingTypeId)
    },
    timedGame() {
      return this.bookingType && this.bookingType.game_type == 'timed'
    },
    ready() {
      return this.initialized && this.openTime && this.closeTime && this.date && this.time && (this.adults || this.juniors) && (this.games || this.duration)
    },
    players() {
      return this.adults + this.juniors
    },
    nearestTime() {
      if (!this.openTime)
        return

      const now = new Date()
      now.setHours(now.getHours() + 1)
      const time = this.openTime > now ? this.openTime : now

      const nearestTo = parseInt(this.timeStep.split(':')[1])
      return roundToNearestMinutes(time, { nearestTo })
    },
    timeStep() {
      const defaultTimeStep = this.settings.time_step || '00:30'
      return this.bookingType ? (this.bookingType.time_step || defaultTimeStep) : defaultTimeStep
    },
    minTime() {
      return this.nearestTime ? this.timeForComponent(this.nearestTime) : null
    },
    maxTime() {
      return this.closeTime ? this.timeForComponent(this.closeTime) : null
    },
    minGames() {
      return this.bookingType ? this.bookingType.min_games : 1
    },
    maxGames() {
      return this.bookingType ? this.bookingType.max_games : 2
    },
    minHour() {
      return this.nearestTime ? this.hourAsInteger(this.nearestTime) : null
    },
    maxHour() {
      return this.closeTime ? this.hourAsInteger(this.closeTime) : null
    },
    playerRange() {
      var range = []
      for (let i = this.minPlayers; i <= this.maxPlayers; i++) {
        range.push(i)
      }
      return range
    }
  },
  data() {
    return {
      initialized: false,
      promoCodeValue: null,
      today: new Date(),
      openTime: null,
      closeTime: null,
      mobileDateTime: new Date(),
      minPlayers: 0,
      maxPlayers: 100,
      pickerOptions: {
        disabledDate: this.disabledDate
      }
    }
  },
  components: {
    scenarios,
    'mt-datetime-picker': DatetimePicker,
    'mt-picker': Picker
  }
}
</script>

<style lang="scss" scoped>
  @import "../styles/settings.scss";
  @import "../styles/mixins.scss";
</style>
